import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useQueryFilter from 'hooks/useQueryFilter';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import { Option } from 'models/Global';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import { FIRST_PAGE } from 'data/global';

import './DealsFilter.styles.scss';

type DealsFilterProps = {
  className?: string;
  options: Option[];
  filterKey: string;
  pageKey?: string;
  variant?: NavigationVariants;
};

const DealsFilter: React.FC<DealsFilterProps> = (props) => {
  const {
    className,
    options,
    filterKey,
    pageKey,
    variant = NAVIGATION_VARIANTS.TERTIARY,
  } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const classes = classNames('deals-filter', className);

  const { t } = useTranslation();

  const { params, onFiltersApply, applyQueryFilter } = useQueryFilter();

  const selectedDealRoomId = params[filterKey];

  const selectedOptions = useMemo(
    () => options.find(({ value }) => value === selectedDealRoomId),
    [selectedDealRoomId, options],
  );

  const triggerTitle = useMemo(
    () => selectedOptions?.label || t('allDeals'),
    [selectedOptions?.label, t],
  );

  const handleOnFilterClick = useCallback(
    (filter: string) => {
      onFiltersApply(filterKey, filter);
      if (pageKey) applyQueryFilter(pageKey, FIRST_PAGE);
      dropdown?.setIsOpen(false);
    },
    [dropdown, filterKey, pageKey, onFiltersApply, applyQueryFilter],
  );

  if (options.length < 2) return null;

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      variant={variant}
      className={classes}
      trigerTitle={triggerTitle}
      renderAsPortal
      portalClassName="deals-filter__portal"
    >
      <ul>
        {[{ value: undefined, label: t('allDeals') }, ...options].map(
          ({ label, value }) => (
            <li
              key={value}
              className={classNames({
                selected: selectedOptions?.value === value,
              })}
              onClick={() => handleOnFilterClick(value)}
            >
              {label}
            </li>
          ),
        )}
      </ul>
    </FilterActionWrapper>
  );
};

export default DealsFilter;
